import React from 'react'
import { Modal, Button, Alert } from 'react-bootstrap'
import { AlertTriangle, CheckCircle, XCircle, Edit3, Lightbulb } from 'lucide-react'

interface FixRejectionsInfoModalProps {
  show: boolean
  onHide: () => void
  onContinueAnyway: () => void
  onGoBack: () => void
  rejectedCount: number
}

const FixRejectionsInfoModal: React.FC<FixRejectionsInfoModalProps> = ({
  show,
  onHide,
  onContinueAnyway,
  onGoBack,
  rejectedCount,
}) => {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#1a1a1a', borderColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        <Modal.Title className="d-flex align-items-center gap-2 text-white">
          <AlertTriangle size={24} className="text-warning" />
          Help Your Model Learn Better
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#1a1a1a', color: '#e0e0e0' }}>
        <Alert variant="warning" className="d-flex align-items-start gap-2">
          <Lightbulb size={20} className="flex-shrink-0 mt-1" />
          <div>
            <strong>
              You have {rejectedCount} rejected prediction{rejectedCount !== 1 ? 's' : ''}
            </strong>
            <p className="mb-0 mt-2">
              Simply rejecting predictions without providing corrections will not help your model
              improve, and may actually make it perform worse.
            </p>
          </div>
        </Alert>

        <div className="mt-4">
          <h6 className="text-white mb-3">Why Fixing Rejections Matters:</h6>

          <div
            className="d-flex gap-3 mb-3 p-3"
            style={{
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              borderRadius: '8px',
              borderLeft: '3px solid #dc3545',
            }}
          >
            <XCircle size={24} className="text-danger flex-shrink-0" />
            <div>
              <strong className="text-danger">Rejection Only:</strong>
              <p className="mb-0 small mt-1">
                The model only learns what it got wrong, but not what the correct answer should be.
                This can lead to confusion and degraded performance in future predictions.
              </p>
            </div>
          </div>

          <div
            className="d-flex gap-3 mb-3 p-3"
            style={{
              backgroundColor: 'rgba(25, 135, 84, 0.1)',
              borderRadius: '8px',
              borderLeft: '3px solid #198754',
            }}
          >
            <CheckCircle size={24} className="text-success flex-shrink-0" />
            <div>
              <strong className="text-success">Rejection + Correction:</strong>
              <p className="mb-0 small mt-1">
                The model learns what was wrong AND what the correct answer is. This provides
                valuable training data that directly improves accuracy.
              </p>
            </div>
          </div>

          <div
            className="d-flex gap-3 p-3"
            style={{
              backgroundColor: 'rgba(13, 110, 253, 0.1)',
              borderRadius: '8px',
              borderLeft: '3px solid #0d6efd',
            }}
          >
            <Edit3 size={24} className="text-info flex-shrink-0" />
            <div>
              <strong className="text-info">Best Practice:</strong>
              <ul className="mb-0 small mt-1 ps-3">
                <li>Correct misclassified objects by adjusting bounding boxes and labels</li>
                <li>Add annotations for objects the AI completely missed</li>
                <li>Remove any duplicate or hallucinated predictions</li>
                <li>Ensure all visible objects of interest are annotated</li>
              </ul>
            </div>
          </div>
        </div>

        <Alert variant="info" className="mt-4 mb-0">
          <small>
            <strong>💡 Tip:</strong> Click "Go Back & Fix" to open the full annotation editor where
            you can correct rejected items and add missing annotations. Your model will thank you!
          </small>
        </Alert>
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: '#1a1a1a', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <Button variant="outline-secondary" onClick={onContinueAnyway}>
          Continue Anyway (Not Recommended)
        </Button>
        <Button
          variant="primary"
          onClick={onGoBack}
          style={{
            backgroundColor: '#9333ea',
            borderColor: '#9333ea',
          }}
        >
          <Edit3 size={16} className="me-2" />
          Go Back & Fix Rejections
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default FixRejectionsInfoModal
